import type { MaskedTextBoxEvent } from "@tql/ui-components-react/kendo/inputs";
import { Error, Label } from "@tql/ui-components-react/kendo/labels";
import {
	MaskedTextBox,
	TextBox,
} from "@tql/ui-components-react/tql/inputs/beta";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { useDebounceCallback } from "usehooks-ts";

import type { SelectedAddressBookItems } from "~/features/quote/context/quote-address-context.tsx";
import { useQuoteAddressContext } from "~/features/quote/context/quote-address-context.tsx";
import type { SubGroupKey } from "~/shared/types/quote-details-types";

import styles from "./contact-row.module.css";

type ContactRowProps = {
	type: SubGroupKey;
	hasFieldError: (field: string) => boolean;
	onClearFieldError: (field: string) => void;
};

export function ContactRow({
	type,
	hasFieldError,
	onClearFieldError,
}: Readonly<ContactRowProps>) {
	const CONTACT_NAME_MAX_LENGTH = 50;
	const { selectedAddressBookItems, updateSelectedAddressBookItem } =
		useQuoteAddressContext();
	const addressBookItem =
		selectedAddressBookItems[type as keyof SelectedAddressBookItems];
	const stopType = type === "pickup" ? 1 : 2;
	const detail = useMemo(() => {
		return addressBookItem?.details.find((d) => d.stopType === stopType);
	}, [addressBookItem?.details, stopType]);
	const contactName = detail?.contactName ?? "";
	const isContactNameOverLimit = contactName.length > CONTACT_NAME_MAX_LENGTH;
	const contactNameErrorKey = `${type}ContactNameLength`;
	const showContactNameError =
		isContactNameOverLimit || hasFieldError(contactNameErrorKey);

	// Kendo's masked input re-applies its mask and caret on every render. Pushing
	// each keystroke straight into the address context re-rendered this row twice
	// per key, which dropped digits during key auto-repeat and backspace. The
	// field now owns its value locally and the context update is debounced, so
	// typing causes no parent re-render at all.
	const externalContactPhone = detail?.contactPhone ?? "";
	const [contactPhone, setContactPhone] = useState(externalContactPhone);
	const isPhoneFocusedRef = useRef(false);

	// Kept in a ref so the debounced callback identity stays stable; re-creating
	// it would throw away the pending timer before it fires.
	const updateAddressBookItemRef = useRef(updateSelectedAddressBookItem);
	updateAddressBookItemRef.current = updateSelectedAddressBookItem;

	// Identifies the stop a queued keystroke belongs to. Applying a saved address
	// (or re-hydrating the quote) replaces the whole item, so a pending update
	// must never be written to the address that took its place.
	const stopIdentity = [
		addressBookItem?.addressBookItemId ?? 0,
		addressBookItem?.companyName ?? "",
		addressBookItem?.address1 ?? "",
		addressBookItem?.postalCode ?? "",
	].join("|");
	const stopIdentityRef = useRef(stopIdentity);
	stopIdentityRef.current = stopIdentity;
	const pendingStopIdentityRef = useRef<string | null>(null);
	const externalContactPhoneRef = useRef(externalContactPhone);
	externalContactPhoneRef.current = externalContactPhone;

	const pushContactPhone = useCallback(
		(value: string) => {
			const originatingStop = pendingStopIdentityRef.current;
			pendingStopIdentityRef.current = null;
			if (
				originatingStop !== null &&
				originatingStop !== stopIdentityRef.current
			) {
				return;
			}
			updateAddressBookItemRef.current(type, "contactPhone", value);
		},
		[type],
	);
	const debouncedPushContactPhone = useDebounceCallback(pushContactPhone, 250);

	useEffect(() => {
		// Only adopt external values while the user is not editing (quote
		// hydration, address-book selection, form reset).
		if (!isPhoneFocusedRef.current) {
			setContactPhone(externalContactPhone);
		}
	}, [externalContactPhone]);

	useEffect(() => {
		// A different address now occupies this stop: drop the keystroke queued for
		// the previous one - even mid-edit - and show the new stop's phone.
		debouncedPushContactPhone.cancel();
		pendingStopIdentityRef.current = null;
		setContactPhone(externalContactPhoneRef.current);
	}, [stopIdentity, debouncedPushContactPhone]);

	useEffect(() => {
		// Don't lose a pending keystroke if the row unmounts (e.g. navigating
		// straight from typing into the tender flow).
		return () => debouncedPushContactPhone.flush();
	}, [debouncedPushContactPhone]);

	return (
		<div className={"pickup-delivery-row"}>
			<div
				className={`pickup-delivery-contact-name-wrapper ${styles.contactNameWrapper}`}
			>
				<Label editorId={`${type}ContactName`}>Contact Name</Label>
				<TextBox
					id={`${type}ContactName`}
					name={`${type}ContactName`}
					onChange={(e) => {
						onClearFieldError(contactNameErrorKey);
						updateSelectedAddressBookItem(
							type,
							"contactName",
							e.target.value as string,
						);
					}}
					valid={!showContactNameError}
					value={contactName}
				/>
				<div className={styles.contactRowErrorsWrapper}>
					{showContactNameError ? (
						<Error>Cannot exceed character limit.</Error>
					) : (
						<span />
					)}
					{contactName.length > 0 ? (
						<span
							className={`${styles.charCounter} ${
								isContactNameOverLimit ? styles.charCounterOverLimit : ""
							}`}
						>
							{contactName.length}/{CONTACT_NAME_MAX_LENGTH}
						</span>
					) : null}
				</div>
			</div>
			<div className={"pickup-delivery-contact-phone-wrapper"}>
				<Label editorId={`${type}ContactPhone`}>
					Contact Phone{" "}
					{type == "delivery" ? <span className={"required"}>*</span> : null}
				</Label>
				<MaskedTextBox
					id={`${type}ContactPhone`}
					mask={"(000) 000-0000"}
					name={`${type}ContactPhone`}
					onBlur={() => {
						isPhoneFocusedRef.current = false;
						// Commit immediately so validation and tendering never read a
						// value the user has already finished typing.
						debouncedPushContactPhone.flush();
					}}
					onChange={(e: MaskedTextBoxEvent) => {
						const nextValue = e.target.value ?? "";
						// Commit synchronously; a deferred render lets the next keystroke
						// read a stale value and drop a digit.
						flushSync(() => {
							setContactPhone(nextValue);
						});
						if (hasFieldError(`${type}ContactPhone`)) {
							onClearFieldError(`${type}ContactPhone`);
						}
						pendingStopIdentityRef.current = stopIdentity;
						debouncedPushContactPhone(nextValue);
					}}
					onFocus={() => {
						isPhoneFocusedRef.current = true;
					}}
					placeholder={"(___) ___-____"}
					valid={!hasFieldError(`${type}ContactPhone`)}
					value={contactPhone}
				/>
				{hasFieldError(`${type}ContactPhone`) ? <Error>Required</Error> : null}
			</div>
			<div className={"pickup-delivery-ext-wrapper"}>
				<Label editorId={`${type}Ext`}>EXT</Label>
				<TextBox
					id={`${type}Ext`}
					maxLength={10}
					name={`${type}Ext`}
					onChange={(e) =>
						updateSelectedAddressBookItem(
							type,
							"contactExtension",
							e.target.value as string,
						)
					}
					pattern={"[0-9]*"}
					value={detail?.contactExtension ?? ""}
				/>
			</div>
		</div>
	);
}
